import PropTypes from "prop-types";
import { encode, decode } from 'html-entities';
import { smartFetch } from '../foundation/smartfetch';
import { buildUrl } from '../foundation/urlbuilder';
const debug = false;
const ctfQSParams = "?fm=webp&q=50";
/* ========== CALL CONTENTFUL DELIVERY API ========== */
/**
 * callContentfulDeliveryAPI — Perform an HTTP GET against a fully-formed Contentful Delivery API URL and return parsed JSON.
 *
 * @param {string} [props.full_url] - Full request URL to the Contentful Delivery API (include access token / query params).
 */
callContentfulDeliveryAPI.propTypes = {
    /** Full request URL to the Contentful Delivery API */
    full_url: PropTypes.string.isRequired,
};
export async function callContentfulDeliveryAPI(props) {
    if (debug)
        console.log("Calling Contentful Delivery API:", props.full_url);
    try {
        const json = await smartFetch(props.full_url);
        return json;
    }
    catch (error) {
        if (error instanceof Error) {
            console.error(error.message);
        }
        else {
            console.error(error);
        }
    }
    return null;
}
/* ========== GET CONTENTFUL CARDS ========== */
/**
 * getContentfulEntries — Fetch entries from Contentful using the supplied API configuration.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object.
 * @param {string} [props.proxyURL] - Optional proxy base URL to route requests through.
 * @param {string} [props.base_url] - Contentful CDN base URL (e.g., https://cdn.contentful.com).
 * @param {string} [props.space_id] - Contentful space ID.
 * @param {string} [props.environment] - Contentful environment name (e.g., 'master').
 * @param {string} [props.delivery_access_token] - Delivery API access token for read requests.
 */
getContentfulEntries.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        /** Optional proxy base URL */
        proxyURL: PropTypes.string,
        /** Contentful CDN base URL */
        base_url: PropTypes.string.isRequired,
        /** Contentful space ID */
        space_id: PropTypes.string.isRequired,
        /** Contentful environment */
        environment: PropTypes.string.isRequired,
        /** Delivery API access token */
        delivery_access_token: PropTypes.string.isRequired,
    }).isRequired,
};
export async function getContentfulEntries(props) {
    if (!props?.apiProps || !props.apiProps.base_url || !props.apiProps.space_id || !props.apiProps.environment || !props.apiProps.delivery_access_token) {
        if (debug)
            console.warn('getContentfulEntries: missing apiProps or required Contentful config. Skipping fetch.');
        return null;
    }
    const { base_url, space_id, environment, delivery_access_token } = props.apiProps;
    const full_url = buildUrl({
        baseUrl: base_url,
        pathSegments: ['spaces', space_id, 'environments', environment, 'entries'],
        params: { access_token: delivery_access_token }
    });
    return await callContentfulDeliveryAPI({ full_url });
}
/*
https://www.contentful.com/developers/docs/references/content-delivery-api/#/reference/entries/entries-collection/get-all-entries-of-a-space/console/js-plain
*/
/* ========== GET CONTENTFUL CARDS BY TYPE ========== */
/**
 * getContentfulEntriesByType — Filter entries by Contentful content type using provided API configuration.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object.
 * @param {string} [props.contentType] - Target content type ID to filter entries by.
 */
getContentfulEntriesByType.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string.isRequired,
        space_id: PropTypes.string.isRequired,
        environment: PropTypes.string.isRequired,
        delivery_access_token: PropTypes.string.isRequired,
    }).isRequired,
    /** Content type ID to filter by */
    contentType: PropTypes.string.isRequired,
};
export async function getContentfulEntriesByType(props) {
    const allEntries = await getContentfulEntries({ apiProps: props.apiProps });
    if (!allEntries || !allEntries.items) {
        return { items: [] };
    }
    const typeEntries = [];
    for (const item of allEntries.items) {
        if (item.sys.contentType.sys.id == props.contentType) {
            typeEntries.push(item);
        }
    }
    allEntries.items = typeEntries;
    return allEntries;
}
/* ========== GET CONTENTFUL CARDS BY TYPE ========== */
/**
 * getContentfulContentType — Retrieve metadata for a Contentful content type.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object.
 * @param {string} [props.contentType] - Contentful content type ID to retrieve.
 */
getContentfulContentType.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string,
        space_id: PropTypes.string,
        environment: PropTypes.string,
        delivery_access_token: PropTypes.string,
    }).isRequired,
    /** Content type ID to retrieve */
    contentType: PropTypes.string.isRequired,
};
export async function getContentfulContentType(props) {
    const { base_url, space_id, environment, delivery_access_token } = props.apiProps;
    if (!base_url || !space_id || !environment) {
        throw new Error('Contentful API properties not configured: base_url, space_id, or environment');
    }
    const full_url = buildUrl({
        baseUrl: base_url,
        pathSegments: ['spaces', space_id, 'environments', environment, 'content_types', props.contentType],
        params: { access_token: delivery_access_token }
    });
    return await callContentfulDeliveryAPI({ full_url });
}
/* ========== GET CONTENTFUL ENTRY BY ENTRY ID ========== */
/**
 * getContentfulEntryByEntryID — Fetch a single Contentful entry by its entry ID.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object.
 * @param {string} [props.entry_id] - Contentful entry ID to fetch.
 */
getContentfulEntryByEntryID.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string,
        space_id: PropTypes.string,
        environment: PropTypes.string,
        delivery_access_token: PropTypes.string,
    }).isRequired,
    /** Target entry ID */
    entry_id: PropTypes.string.isRequired,
};
export async function getContentfulEntryByEntryID(props) {
    const { base_url, space_id, environment, delivery_access_token } = props.apiProps;
    if (!base_url || !space_id || !environment) {
        throw new Error('Contentful API properties not configured: base_url, space_id, or environment');
    }
    const full_url = buildUrl({
        baseUrl: base_url,
        pathSegments: ['spaces', space_id, 'environments', environment, 'entries', props.entry_id],
        params: { access_token: delivery_access_token }
    });
    return await callContentfulDeliveryAPI({ full_url });
}
/**
 * getContentfulAssetByAssetID — Fetch a single Contentful asset by its asset ID.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object.
 * @param {string} [props.asset_id] - Contentful asset ID to fetch.
 */
getContentfulAssetByAssetID.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string,
        space_id: PropTypes.string,
        environment: PropTypes.string,
        delivery_access_token: PropTypes.string,
    }).isRequired,
    /** Target asset ID */
    asset_id: PropTypes.string.isRequired,
};
export async function getContentfulAssetByAssetID(props) {
    const { base_url, space_id, environment, delivery_access_token } = props.apiProps;
    if (!base_url || !space_id || !environment) {
        throw new Error('Contentful API properties not configured: base_url, space_id, or environment');
    }
    const full_url = buildUrl({
        baseUrl: base_url,
        pathSegments: ['spaces', space_id, 'environments', environment, 'assets', props.asset_id],
        params: { access_token: delivery_access_token }
    });
    return await callContentfulDeliveryAPI({ full_url });
}
export async function getContentfulVideoMetadata(videoUrl, contentfulConfig) {
    if (!videoUrl) {
        return null;
    }
    const contentful = contentfulConfig;
    if (!contentful?.base_url || !contentful?.space_id || !contentful?.environment || !contentful?.delivery_access_token) {
        if (debug)
            console.warn('getContentfulVideoMetadata: missing Contentful config.');
        return null;
    }
    let assetId;
    try {
        const parsed = new URL(videoUrl);
        const segments = parsed.pathname.split('/').filter(Boolean);
        assetId = segments[1];
    }
    catch {
        assetId = undefined;
    }
    if (!assetId) {
        return null;
    }
    const asset = await getContentfulAssetByAssetID({
        apiProps: contentful,
        asset_id: assetId,
    });
    if (!asset) {
        return null;
    }
    const fields = asset.fields ?? {};
    const sys = asset.sys ?? {};
    const file = fields.file ?? {};
    const duration = file?.details?.duration ? String(file.details.duration) : undefined;
    return {
        contentUrl: videoUrl,
        poster: normalizeContentfulAssetUrl(file?.url),
        title: fields.title || file?.fileName || undefined,
        name: fields.title || file?.fileName || undefined,
        description: fields.description,
        uploadDate: sys.createdAt || sys.updatedAt || undefined,
        duration,
    };
}
/* ========== GET CONTENTFUL CARD BY FIELD ========== */
export async function getContentfulEntryByField(params) {
    // const cards: any = await getContentfulEntriesData();
    const searchVal = decodeURIComponent(params.searchVal);
    for (const item of params.cards.items) {
        if (item?.fields[params.searchField]?.toLowerCase() == searchVal.toLowerCase()) {
            return item;
        }
    }
    return null;
}
/* ========== GET CONTENTFUL CARD TITLES ========== */
/**
 * getContentfulFieldValues — Return unique values for a field from entries of a given content type.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object.
 * @param {string} [props.contentType] - Contentful content type ID to inspect.
 * @param {string} [props.field] - Field name to extract values from.
 */
getContentfulFieldValues.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string.isRequired,
        space_id: PropTypes.string.isRequired,
        environment: PropTypes.string.isRequired,
        delivery_access_token: PropTypes.string.isRequired,
    }).isRequired,
    /** Content type ID to query */
    contentType: PropTypes.string.isRequired,
    /** Field name to extract values from */
    field: PropTypes.string.isRequired,
};
export async function getContentfulFieldValues(props) {
    const cards = await getContentfulEntriesByType({
        apiProps: props.apiProps,
        contentType: props.contentType,
    });
    const fieldVals = cards.items.map(function (card) {
        return card.fields[props.field];
    });
    return fieldVals;
}
/* ========== GET CONTENTFUL IMAGES FROM CARDS ========== */
export function normalizeContentfulAssetUrl(url) {
    if (!url || typeof url !== 'string')
        return undefined;
    if (url.startsWith('//'))
        return `https:${url}`;
    if (url.startsWith('/'))
        return `https://images.ctfassets.net${url}`;
    return url;
}
/**
 * getContentfulImagesFromEntries — Resolve image asset URLs and alt text for entry image references.
 *
 * @param {any} [props.images] - Entry image references (items with sys.id that map to assets).
 * @param {any} [props.assets] - Array of Contentful asset objects to resolve URLs from.
 */
getContentfulImagesFromEntries.propTypes = {
    /** Entry image references to resolve */
    images: PropTypes.any.isRequired,
    /** Contentful asset objects containing file URLs and descriptions */
    assets: PropTypes.any.isRequired,
};
export async function getContentfulImagesFromEntries(props) {
    const images = props.images ? (Array.isArray(props.images) ? props.images : [props.images]) : [];
    const assets = Array.isArray(props.assets)
        ? props.assets
        : props.assets && Array.isArray(props.assets.items)
            ? props.assets.items
            : [];
    const imageURLs = [];
    for (const image of images) {
        let asset;
        if (image?.sys?.id) {
            asset = assets.find((item) => item?.sys?.id === image.sys.id);
        }
        else if (image?.fields?.file?.url) {
            asset = image;
        }
        if (!asset)
            continue;
        const fileUrl = asset.fields?.file?.url;
        if (!fileUrl)
            continue;
        const normalizedUrl = normalizeContentfulAssetUrl(fileUrl);
        imageURLs.push({
            image: normalizedUrl + ctfQSParams,
            imageAlt: asset.fields?.description || asset.fields?.title || undefined,
        });
    }
    return imageURLs;
}
/* ========== GET CONTENTFUL ASSETS ========== */
/**
 * getContentfulAssets — Fetch asset records from Contentful for the configured space/environment.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object.
 */
getContentfulAssets.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string,
        space_id: PropTypes.string,
        environment: PropTypes.string,
        delivery_access_token: PropTypes.string,
    }).isRequired,
};
export async function getContentfulAssets(props) {
    const { base_url, space_id, environment, delivery_access_token } = props.apiProps;
    const full_url = buildUrl({
        baseUrl: base_url,
        pathSegments: ['spaces', space_id, 'environments', environment, 'assets'],
        params: { access_token: delivery_access_token }
    });
    return await callContentfulDeliveryAPI({ full_url });
}
/* ========== GET CONTENTFUL ASSETS BY TAG ========== */
/**
 * getContentfulAssetsByTag — Fetch asset records matching a Contentful tag ID.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object.
 * @param {string} [props.tag] - Contentful tag ID to filter by.
 * @param {string} [props.orderBy] - Contentful asset field to sort by; defaults to title.
 */
getContentfulAssetsByTag.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string.isRequired,
        space_id: PropTypes.string.isRequired,
        environment: PropTypes.string.isRequired,
        delivery_access_token: PropTypes.string.isRequired,
    }).isRequired,
    /** Contentful tag ID */
    tag: PropTypes.string.isRequired,
    /** Contentful asset field used for ordering */
    orderBy: PropTypes.string,
};
export async function getContentfulAssetsByTag(props) {
    const { base_url, space_id, environment, delivery_access_token } = props.apiProps;
    const orderBy = props.orderBy ?? 'title';
    const fieldName = orderBy.startsWith('fields.') ? orderBy.slice('fields.'.length) : orderBy;
    if (!base_url || !space_id || !environment || !delivery_access_token || !props.tag) {
        if (debug)
            console.warn('getContentfulAssetsByTag: missing required Contentful configuration or tag. Skipping fetch.');
        return null;
    }
    const full_url = buildUrl({
        baseUrl: base_url,
        pathSegments: ['spaces', space_id, 'environments', environment, 'assets'],
        params: {
            access_token: delivery_access_token,
            'metadata.tags.sys.id[in]': props.tag,
        }
    });
    const response = await callContentfulDeliveryAPI({ full_url });
    if (!Array.isArray(response?.items)) {
        return response;
    }
    return {
        ...response,
        items: response.items
            .map((item, index) => ({ item, index }))
            .sort((a, b) => {
            const aValue = String(a.item?.fields?.[fieldName] ?? '');
            const bValue = String(b.item?.fields?.[fieldName] ?? '');
            return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' }) || a.index - b.index;
        })
            .map(({ item }) => item),
    };
}
/* ========== GET CONTENTFUL ASSET URLS ========== */
/**
 * getContentfulAssetURLs — Return resolved asset URLs and metadata using provided API configuration.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object.
 */
getContentfulAssetURLs.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string,
        space_id: PropTypes.string,
        environment: PropTypes.string,
        delivery_access_token: PropTypes.string,
    }).isRequired,
};
export async function getContentfulAssetURLs(props) {
    const assetURLs = [];
    const assets = await getContentfulAssets(props);
    for (const asset of assets.items) {
        assetURLs.push({
            image: asset.fields.file.url + ctfQSParams,
            imageAlt: asset.fields.description || asset.fields.title || '',
        });
    }
    if (debug)
        console.log("Asset URLs: ", assetURLs);
    return assetURLs;
}
/* ========== GET CONTENTFUL DISCOUNT CODES ========== */
/**
 * getContentfulDiscountCodes — Retrieve discount code entries from Contentful using the supplied API configuration.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object (base_url, space_id, environment, access tokens).
 * @param {string} [props.contentType] - Contentful content type ID for discount codes (default: 'discountCodes').
 */
getContentfulDiscountCodes.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string.isRequired,
        space_id: PropTypes.string.isRequired,
        environment: PropTypes.string.isRequired,
        delivery_access_token: PropTypes.string.isRequired,
    }).isRequired,
    /** Contentful content type ID to fetch */
    contentType: PropTypes.string.isRequired,
};
export async function getContentfulDiscountCodes(props) {
    const contentType = "discountCodes";
    try {
        if (debug)
            console.log("Fetching Discount Codes");
        const response = await getContentfulEntriesByType({
            apiProps: props.apiProps,
            contentType: props.contentType
        });
        if (!response || !response.items) {
            console.error(`HTTP error! status: ${response.status}`);
            return [];
        }
        const filteredItems = response.items.filter((item) => {
            return item.sys.contentType.sys.id === contentType;
        });
        const discountCodes = filteredItems.map((item) => ({
            codeName: item.fields.codeName,
            codeDescription: item.fields.codeDescription,
            codeType: item.fields.codeType,
            codeStart: item.fields.codeStart,
            codeEnd: item.fields.codeEnd,
            codeValue: item.fields.codeValue,
        }));
        return discountCodes;
    }
    catch (error) {
        console.error('Error fetching token:', error);
        return [];
    }
}
/* ========== GET CONTENTFUL REVIEWS AS SCHEMA ========== */
/**
 * getContentfulReviewsSchema — Retrieve review entries from Contentful and convert them to schema.org/Review JSON-LD format.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object (base_url, space_id, environment, access tokens).
 * @param {string} [props.itemName] - Name of the item/product being reviewed (e.g., 'Epoxy Flooring Service').
 * @param {string} [props.itemType] - Schema type of the item being reviewed (default: 'Product').
 * @param {string} [props.publisherName] - Name of the organization publishing the reviews.
 */
getContentfulReviewsSchema.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string.isRequired,
        space_id: PropTypes.string.isRequired,
        environment: PropTypes.string.isRequired,
        delivery_access_token: PropTypes.string.isRequired,
    }).isRequired,
    /** Name of the item/product being reviewed */
    itemName: PropTypes.string.isRequired,
    /** Schema type of the item being reviewed */
    itemType: PropTypes.string,
    /** Name of the organization publishing the reviews */
    publisherName: PropTypes.string,
};
export async function getContentfulReviewsSchema(props) {
    const contentType = "reviews";
    const itemName = props.itemName;
    const itemType = ['Product', 'LocalBusiness', 'Organization'].includes(props.itemType ?? '') ? props.itemType : "LocalBusiness";
    const publisherName = props.publisherName;
    try {
        if (debug)
            console.log("Fetching Reviews and converting to schema");
        const response = await getContentfulEntriesByType({
            apiProps: props.apiProps,
            contentType: contentType
        });
        if (!response || !response.items) {
            console.error("No reviews found in Contentful");
            return [];
        }
        const reviewSchemas = response.items.map((item) => {
            // Extract reviewer name from "fields.reviewer" which comes as " - Name"
            const reviewerNameRaw = item.fields.reviewer || "Anonymous";
            const reviewerName = reviewerNameRaw.replace(/^\s*-\s*/, "").trim();
            // Convert Contentful date to ISO format (remove quotes if present)
            const publishDate = new Date(item.sys.createdAt).toISOString().split("T")[0];
            // Create Review schema object
            const reviewSchema = {
                "@context": "https://schema.org/",
                "@type": "Review",
                "name": "", // Will be extracted or generated from review body
                "reviewBody": item.fields.description?.replace(/^"|"$/g, "").trim() || "",
                "datePublished": publishDate,
                "author": {
                    "@type": "Person",
                    "name": reviewerName
                },
                "itemReviewed": {
                    "@type": itemType,
                    "name": itemName
                },
                "reviewRating": {
                    "@type": "Rating",
                    "ratingValue": "5",
                    "bestRating": "5",
                    "worstRating": "1"
                }
            };
            // Generate name from first sentence of review or use generic title
            if (reviewSchema.reviewBody) {
                const sentences = reviewSchema.reviewBody.split(/[.!?]/);
                reviewSchema.name = sentences[0].trim().substring(0, 100) || "Review";
            }
            // Add publisher if provided
            if (publisherName) {
                reviewSchema.publisher = {
                    "@type": "Organization",
                    "name": publisherName
                };
            }
            return reviewSchema;
        });
        if (debug)
            console.log("Review Schemas: ", reviewSchemas);
        return reviewSchemas;
    }
    catch (error) {
        console.error('Error fetching reviews:', error);
        return [];
    }
}
/* ========== GET CONTENTFUL PRODUCT AS SCHEMA ========== */
/**
 * getContentfulProductSchema — Retrieve a product entry from Contentful and convert it to schema.org/Product JSON-LD format.
 *
 * @param {shape} [props.apiProps] - Contentful API configuration object (base_url, space_id, environment, access tokens).
 * @param {string} [props.productId] - The product ID field value to search for (e.g., 'PIX-000779').
 * @param {string} [props.siteUrl] - Optional base site URL for the offer URL.
 * @param {function} [props.getAssetUrl] - Optional function to transform asset URLs.
 */
getContentfulProductSchema.propTypes = {
    /** Contentful API configuration */
    apiProps: PropTypes.shape({
        proxyURL: PropTypes.string,
        base_url: PropTypes.string.isRequired,
        space_id: PropTypes.string.isRequired,
        environment: PropTypes.string.isRequired,
        delivery_access_token: PropTypes.string.isRequired,
    }).isRequired,
    /** Product ID to search for */
    productId: PropTypes.string.isRequired,
    /** Optional site URL for offer */
    siteUrl: PropTypes.string,
    /** Optional function to transform asset URLs */
    getAssetUrl: PropTypes.func,
};
export async function getContentfulProductSchema(props) {
    const contentType = "item";
    const productId = props.productId;
    const siteUrl = props.siteUrl || '';
    const getAssetUrl = props.getAssetUrl;
    try {
        if (debug)
            console.log("Fetching Product and converting to schema");
        const response = await getContentfulEntriesByType({
            apiProps: props.apiProps,
            contentType: contentType
        });
        if (!response || !response.items) {
            console.error("No products found in Contentful");
            return null;
        }
        // Use existing helper function to find product by ID field
        const product = await getContentfulEntryByField({
            cards: response,
            searchField: 'id',
            searchVal: productId
        });
        if (!product) {
            console.error(`Product with ID ${productId} not found`);
            return null;
        }
        const fields = product.fields;
        const rawGtin = fields.gtin != null ? String(fields.gtin).trim() : '';
        const gtin = rawGtin || undefined;
        const productSchema = {
            name: fields.title || '',
            description: fields.description || '',
            brand: fields.brand ? { name: fields.brand } : undefined,
            sku: fields.sku || productId || undefined,
            mpn: fields.mpn || fields.sku || productId || undefined,
            ...(gtin && { gtin }),
            shippingDetails: {
                isShippable: !(fields.productType === 'EVENT' || fields.itemType === 'EVENT'),
                weight: fields.weight,
                weightUnit: fields.weightUnit || fields.weight_unit,
            },
            offers: {
                url: siteUrl || '',
                priceCurrency: 'USD',
                price: String(fields.price || '0'),
                availability: 'https://schema.org/InStock'
            }
        };
        if (Array.isArray(fields.images) && fields.images.length > 0) {
            productSchema.image = fields.images
                .map((img) => {
                if (typeof img === 'string') {
                    return img;
                }
                if (img?.sys?.id) {
                    return getAssetUrl ? getAssetUrl(img.sys.id) : `https://assets.ctfassets.net/${img.sys.id}`;
                }
                return '';
            })
                .filter((url) => Boolean(url));
        }
        if (fields.model) {
            productSchema.model = fields.model;
        }
        if (fields.color) {
            productSchema.color = fields.color;
        }
        // Add rating based on availability/quantity
        if (fields.quantity !== undefined) {
            const availability = fields.quantity > 0
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock';
            productSchema.offers.availability = availability;
        }
        if (debug)
            console.log("Contentful Product Schema: ", productSchema);
        return productSchema;
    }
    catch (error) {
        console.error('Error fetching product:', error);
        return null;
    }
}
/* ========== CONTENTFUL VALUE < - > SLUG ========== */
/**
 * contentfulValueToSlug — Convert a Contentful field value (e.g. a title) to a URL-safe, HTML-entity-encoded slug.
 * Trims, lowercases, replaces whitespace with dashes, and HTML-entity-encodes special characters.
 *
 * @param {string} value - The raw field value from Contentful.
 * @returns {string} URL and XML-safe slug, e.g. "Epoxy & Floors" → "epoxy-&amp;-floors".
 */
contentfulValueToSlug.propTypes = {
    /** Raw field value to convert to slug */
    value: PropTypes.string.isRequired,
};
export function contentfulValueToSlug(props) {
    const { value } = props;
    return encode(value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-'));
}
/**
 * contentfulSlugToValue — Reverse the encoding from contentfulValueToSlug to recover the original title.
 * Decodes HTML entities and restores spaces from dashes.
 *
 * @param {string} slug - The encoded slug (e.g., "epoxy-&amp;-floors").
 * @returns {string} Original title format (lowercase), e.g. "epoxy & floors".
 */
contentfulSlugToValue.propTypes = {
    /** Encoded slug to decode */
    slug: PropTypes.string.isRequired,
};
export function contentfulSlugToValue(props) {
    const { slug } = props;
    return decode(slug).replace(/-/g, ' ');
}
